import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CloseCircleGreyIcon, SearchIcon } from 'assets';
import { useClickOutside, useReviewTalentsCandidates } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCandidates,
  setPrimaryCandidate,
  toggleComparisonCandidate,
  resetCandidatesComparison
} from 'state/actions/candidatesComparison';
import stylesLocal from './candidate-selection-modal.module.scss';
import { RadioInput } from 'components/form/radio';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { RootReducerState } from 'state/reducers';
import { Routes } from 'router';

interface CandidateSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDone?: (primaryId: string, comparisonIds: string[]) => void;
}

interface OptionType {
  label: string;
  value: string;
}

interface CandidateSelectionFormValues {
  primaryCandidateId: string;
  comparisonCandidateIds: string[];
}

const schema = yup.object({
  primaryCandidateId: yup.string().required('Primary candidate required'),
  comparisonCandidateIds: yup
    .array(yup.string())
    .min(1, 'Select at least 1 candidate to compare')
    .max(4, 'Select up to 4 candidates')
});

const CandidateSelectionModal: React.FC<CandidateSelectionModalProps> = ({
  isOpen,
  onClose,
  onDone
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    candidates = []
  } = useSelector((s: RootReducerState) =>
    (s as any)?.candidatesComparison || { candidates: [], primaryCandidateId: null, comparisonCandidateIds: [] }
  );

  // Form handles all interim state locally (no redux sync while selecting)
  const {
    register,
    watch,
    trigger,
    reset,
    formState: { errors }
  } = useForm<CandidateSelectionFormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      primaryCandidateId: '',
      comparisonCandidateIds: []
    }
  });
  const { id: talentId, id2: reportId } = useParams<{ id: string, id2: string }>();

  const watchPrimary = watch('primaryCandidateId');
  const watchComparisons = watch('comparisonCandidateIds');

  const [search, setSearch] = useState('');

  // Fetch candidates from API
  const { candidates: apiCandidates, isLoading } = useReviewTalentsCandidates({
    assessmentId: talentId,
    search: search,
    pageSize: 100, // Get all candidates for selection
  });

  // Transform API candidates to match the format needed by the modal
  const transformedCandidates = useMemo(() => {
    return apiCandidates.map((candidate) => ({
      id: String(candidate.id),
      name: candidate.full_name,
      score: candidate.total_score ? parseFloat(candidate.total_score) : 0,
      email: candidate.email,
      phone_number: candidate.phone_number,
      created_at: candidate.created_at,
      portfolio: candidate.portfolio,
      resume: candidate.resume,
      bucket: candidate.bucket,
    }));
  }, [apiCandidates]);

  // Load candidates into Redux when API data is available
  useEffect(() => {
    if (isOpen && transformedCandidates.length > 0 && candidates.length === 0) {
      dispatch(setCandidates(transformedCandidates));
    }
  }, [isOpen, transformedCandidates, candidates.length, dispatch]);

  // If modal reopens and redux has previous selections we clear the form (fresh start)
  useEffect(() => {
    if (isOpen) {
      reset({ primaryCandidateId: '', comparisonCandidateIds: [] });
    }
  }, [isOpen, reset]);

  useClickOutside(modalRef, onClose);

  // Use candidates from Redux if available, otherwise use transformed API data
  const candidatesList = candidates.length > 0 ? candidates : transformedCandidates;

  const filtered = useMemo(
    () =>
      candidatesList.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase())
      ),
    [candidatesList, search]
  );

  const primaryOptions: OptionType[] = filtered.map(c => ({
    label: `${c.name} (${c.score}%)`,
    value: c.id
  }));

  const baseCandidate = candidates.find(c => c.id === watchPrimary) || null;

  const comparisonOptions: OptionType[] = filtered
    .filter(c => c.id !== watchPrimary)
    .map(c => ({
      label: `${c.name} (${c.score}%)`,
      value: c.id
    }));

  const handleDone = async () => {
    const valid = await trigger();
    if (!valid) return;
    if (!watchPrimary) return;

    // Persist the final selection to redux now (only once)
    dispatch(resetCandidatesComparison());
    dispatch(setCandidates(candidatesList));
    dispatch(setPrimaryCandidate(watchPrimary));
    (watchComparisons || []).forEach(id => dispatch(toggleComparisonCandidate(id)));

    if (onDone) onDone(watchPrimary, watchComparisons || []);
    navigate(Routes.compareCandidates({ id: talentId, id2: reportId }));
    onClose();
  };

  const handleReset = () => {
    // Just reset local form; keep candidate list
    reset({ primaryCandidateId: '', comparisonCandidateIds: [] });
    setSearch('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[999999]">
      <div className="bg-white rounded-xl sm:w-[370px] w-[90%] mx-auto shadow-lg relative flex flex-col" ref={modalRef}>

        <div className="flex justify-between items-center mb-4 bg-[#D1E9FF] p-5 rounded-t-xl">
          <h2 className="text-lg font-semibold text-gray-800">
            {watchPrimary ? 'Select Candidates' : 'Select Primary Candidate'}
          </h2>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="text-xs text-[#012169] border border-[#012169] !bg-transparent hover:opacity-70 !px-3 !py-1 !h-fit !min-h-fit"
            >
              Reset
            </button>
            <button onClick={onClose} className="!bg-transparent focus:outline-none !p-0 !h-fit !min-h-fit w-fit hover:bg-transparent">
              <CloseCircleGreyIcon className='text-[#5a5a66]' />
            </button>
          </div>
        </div>

        <p className="mx-5 text-sm text-gray-600 mb-3">
          {watchPrimary
            ? `Select up to four(4) other candidates to compare to ${baseCandidate?.name}`
            : 'Select a candidate to set as the primary for comparison'}
        </p>

        <div className="mx-5 flex items-center border border-gray-300 rounded-md p-2 mb-4">
          <SearchIcon className="text-gray-400 mr-2" />
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 border-none focus:outline-none text-sm bg-transparent"
          />
        </div>

        <div className={`px-5 mb-4 max-h-72 overflow-y-auto ${stylesLocal.candidateScroll}`}>
          {isLoading && (
            <p className="text-xs text-[#475467] mb-2">Loading candidates...</p>
          )}
          {!isLoading && !watchPrimary && (
            <>
              {primaryOptions.length === 0 && (
                <p className="text-xs text-[#475467] mb-2">No candidates found.</p>
              )}
              {primaryOptions.length > 0 && (
                <RadioInput
                  name="primaryCandidateId"
                  type="radio"
                  register={register}
                  validatorMessage={errors.primaryCandidateId?.message}
                  label=""
                  options={primaryOptions}
                  parentClassName="!p-0 !m-0"
                  showRequired={false}
                />
              )}
            </>
          )}

          {!isLoading && watchPrimary && (
            <>
              <div className="mb-3">
                <p className="text-xs font-medium text-[#475467] mb-1">Primary Candidate</p>
                <div className="flex justify-between items-center bg-[#F1F5F9] px-3 py-2 rounded">
                  <span className="text-sm">{baseCandidate?.name}</span>
                  <span className="text-sm font-semibold text-[#1E40AF]">
                    {Math.round(baseCandidate?.score || 0)}%
                  </span>
                </div>
              </div>
              <p className="text-xs font-medium text-[#475467] mb-2">Other Candidates</p>
              {comparisonOptions.length === 0 && (
                <p className="text-[11px] text-[#475467] mb-2">No other candidates available.</p>
              )}
              {comparisonOptions.length > 0 && (
                <RadioInput
                  name="comparisonCandidateIds"
                  type="checkbox"
                  register={register}
                  validatorMessage={errors.comparisonCandidateIds?.message as string}
                  label=""
                  options={comparisonOptions}
                  parentClassName="!p-0 !m-0"
                  showRequired={false}
                  disabled={(watchComparisons?.length || 0) >= 4}
                  checkValue={watchComparisons || []}
                />
              )}
              <p className="text-[11px] text-[#475467] mt-2">
                {(watchComparisons?.length || 0)}/4 selected
              </p>
            </>
          )}

          {(errors.primaryCandidateId || errors.comparisonCandidateIds) && (
            <p className="text-[11px] text-red-600 mt-2">
              {errors.primaryCandidateId?.message || errors.comparisonCandidateIds?.message}
            </p>
          )}
        </div>

        <div className='bg-[#D1E9FF] p-5 flex justify-end gap-2 rounded-b-xl'>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white border border-[#012169] text-[#012169] rounded-md text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            disabled={!watchPrimary || !(watchComparisons?.length || 0)}
            onClick={handleDone}
            className={`px-5 py-2 rounded-md text-sm !ml-0 font-medium focus:outline-none ${
              watchPrimary && (watchComparisons?.length || 0)
                ? 'bg-[#012169] text-white hover:bg-[#0120697d]'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export { CandidateSelectionModal };